// Reconoce en lo escrito valores de facets del índice de productos (motor,
// marca del vehículo, categoría, marca de la pieza) para proponerlos como
// filtros: "brake shoes isuzu 4hk1" -> Categoría Brake Shoes, Vehículo ISUZU,
// Motor 4HK1 - 5.2, y el texto que queda se busca como texto.
//
// Todo en el navegador contra el vocabulario de loadFacetValues
// (utils/productSearch.js): una petición al abrir la pestaña y nada más
// mientras se escribe. Sólo coincidencias exactas (salvo mayúsculas,
// separadores y plural en "s"), para no proponer filtros que no se pidieron.
//
// El modelo (NPR, NPR-HD) y el año no se reconocen: no son facets del índice
// (fitment son ids de aplicación).

// Orden de prioridad: si un trozo coincide con varios, gana el primero. ISUZU
// es a la vez marca de vehículo y de pieza, y quien la escribe junto a una
// pieza casi siempre habla del vehículo.
const KINDS = [
  {
    field: "engines",
    label: "Engine",
    // "4HK1 - 5.2" se escribe "4hk1". Sólo códigos con dígitos: "GAS - 6.0"
    // no se propone al escribir "gas".
    keyOf: value => value.split(" - ")[0],
    accept: key => /\d/.test(key)
  },
  { field: "brand_vehicle", label: "Vehicle" },
  {
    field: "hierarchicalCategories.lvl1",
    label: "Category",
    // "Brakes > Brake Shoes" se escribe "brake shoes".
    keyOf: value => value.split(" > ").pop()
  },
  { field: "hierarchicalCategories.lvl0", label: "Category" },
  { field: "brand", label: "Brand" }
];

export const INTENT_FIELDS = KINDS.map(kind => kind.field);

// Frases de hasta tantas palabras ("engine and fuel parts" son 4).
const MAX_WORDS = 4;

// "Brake-Shoes" y "brake shoes" -> "brake shoe": minúsculas, sin separadores
// y sin la "s" final de cada palabra (de 4 letras o más, para no tocar códigos
// cortos). Los separadores se quitan dentro de cada palabra, no entre
// palabras, para que "4hk1" no coincida con "4 hk1".
const normalizeWord = word => {
  const clean = word.toLowerCase().replace(/[^a-z0-9]/g, "");
  return clean.length >= 4 && clean.endsWith("s") ? clean.slice(0, -1) : clean;
};
const normalize = text =>
  String(text)
    .split(/[\s,]+/)
    .map(normalizeWord)
    .filter(Boolean)
    .join(" ");

/**
 * Vocabulario a partir de { campo: [valor, ...] }: Map de clave normalizada a
 * la primera opción por prioridad.
 */
export const buildVocabulary = facetValues => {
  const vocabulary = new Map();
  KINDS.forEach(kind => {
    (facetValues[kind.field] || []).forEach(value => {
      const display = kind.keyOf ? kind.keyOf(value) : value;
      const key = normalize(display);
      if (key.length < 2 || (kind.accept && !kind.accept(key))) return;
      if (vocabulary.has(key)) return;
      vocabulary.set(key, {
        type: kind.field,
        value,
        kind: kind.label,
        display
      });
    });
  });
  return vocabulary;
};

/**
 * Filtros reconocidos en `query`, sin los que ya están aplicados:
 * [{ type, value, kind, display, words, start }]. `words` son las palabras de
 * la búsqueda que se reconocieron, para quitarlas del texto al aplicar el
 * filtro. Primero las frases más largas: "brake shoes" gana a "brake".
 * `skip`: posiciones de palabras que ya son de otra cosa (el vehículo, ver
 * utils/vehicleIntent.js) y no se miran.
 */
export const detectIntent = (query, vocabulary, applied = [], skip = []) => {
  if (!vocabulary || !query) return [];
  const words = String(query)
    .split(/\s+/)
    .filter(Boolean);
  const used = words.map((_, index) => skip.includes(index));
  const isApplied = match =>
    applied.some(s => s.type === match.type && s.value === match.value);
  const found = [];
  for (let size = Math.min(MAX_WORDS, words.length); size >= 1; size--) {
    for (let start = 0; start + size <= words.length; start++) {
      if (used.slice(start, start + size).some(Boolean)) continue;
      const phrase = words.slice(start, start + size);
      const match = vocabulary.get(normalize(phrase.join(" ")));
      if (!match || isApplied(match)) continue;
      if (found.some(f => f.type === match.type && f.value === match.value)) {
        continue;
      }
      for (let i = start; i < start + size; i++) used[i] = true;
      found.push({ ...match, words: phrase, start });
    }
  }
  // En el orden en que aparecen en lo escrito.
  return found.sort((a, b) => a.start - b.start);
};

/** `query` sin las palabras de esas posiciones. */
export const removeWordIndices = (query, indices) => {
  const drop = new Set(indices);
  return String(query)
    .split(/\s+/)
    .filter(Boolean)
    .filter((_, index) => !drop.has(index))
    .join(" ");
};

/** `query` sin las palabras de los filtros aplicados. */
export const removeWords = (query, matches) =>
  removeWordIndices(
    query,
    matches.flatMap(match => match.words.map((_, index) => match.start + index))
  );
